"use client"

import * as React from "react"
import { format, parse, isValid, setYear, setMonth, getYear, getMonth, startOfDay, endOfDay, parseISO } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCurrentBudgetPeriod } from "@/hooks/use-financial"

/** Ícones de navegação do react-day-picker. Ficam no escopo do módulo porque o
 *  `components` do DayPicker os RENDERIZA como componentes (`<IconLeft />`), e
 *  definir inline recria a identidade a cada render. Não recebem props nem
 *  capturam nada do escopo do componente pai. */
const IconeAnterior = () => <ChevronLeft className="h-4 w-4" />;
const IconeProximo = () => <ChevronRight className="h-4 w-4" />;

interface DateInputProps {
  /** Value in ISO format (yyyy-MM-dd) for backend compatibility */
  value?: string
  /** Callback with value in ISO format (yyyy-MM-dd) */
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  /** Minimum year for the year selector */
  minYear?: number
  /** Maximum year for the year selector */
  maxYear?: number
  /** Display mode: 'budget-period' restricts to current period, 'future-only' restricts to dates after budget period */
  mode?: 'budget-period' | 'future-only'
  /** Show single calendar instead of dual calendar */
  showSingleCalendar?: boolean
}

/**
 * DateInput Component
 * 
 * A date input that displays dates in Brazilian format (dd/MM/yyyy)
 * while sending/receiving values in ISO format (yyyy-MM-dd) for backend compatibility.
 * 
 * Features:
 * - Direct typing of dates
 * - Quick year and month selector
 * - Dual calendar view with budget period highlighting
 * - Highlights current date and budget period boundaries
 * - Validates that only dates within the budget period can be selected
 */
export function DateInput({
  value,
  onChange,
  placeholder = "Selecione uma data",
  disabled = false,
  className,
  id,
  minYear = 2020,
  maxYear = 2050,
  mode = 'budget-period',
  showSingleCalendar = false,
}: Readonly<DateInputProps>) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [viewDate, setViewDate] = React.useState<Date>(new Date())
  
  // Get current budget period for highlighting
  const { data: currentPeriod } = useCurrentBudgetPeriod()

  // Convert ISO string (yyyy-MM-dd) to Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    const parsed = parse(value, 'yyyy-MM-dd', new Date())
    return isValid(parsed) ? parsed : undefined
  }, [value])

  // Calculate period dates for validation
  const periodStart = React.useMemo(() => {
    if (!currentPeriod?.start_date) return undefined
    return startOfDay(parseISO(currentPeriod.start_date))
  }, [currentPeriod?.start_date])

  const periodEnd = React.useMemo(() => {
    if (!currentPeriod?.end_date) return undefined
    return endOfDay(parseISO(currentPeriod.end_date))
  }, [currentPeriod?.end_date])

  // Validate if date is within budget period
  const isDateWithinBudgetPeriod = React.useCallback((date: Date): boolean => {
    if (!periodStart || !periodEnd) return true // No restrictions if no period defined
    const d = startOfDay(date)
    return d >= periodStart && d <= periodEnd
  }, [periodStart, periodEnd])

  // Validate if date is after budget period (for future-only mode)
  const isDateAfterBudgetPeriod = React.useCallback((date: Date): boolean => {
    if (!periodEnd) return true // No restrictions if no period defined
    const d = startOfDay(date)
    return d > periodEnd
  }, [periodEnd])

  // Update input value when dateValue changes
  React.useEffect(() => {
    if (dateValue) {
      setInputValue(format(dateValue, "dd/MM/yyyy", { locale: ptBR }))
      setViewDate(dateValue)
    } else {
      setInputValue("")
    }
  }, [dateValue])

  // Handle date selection from calendar
  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Validate date based on mode
      const isValid = mode === 'future-only' 
        ? isDateAfterBudgetPeriod(date) 
        : isDateWithinBudgetPeriod(date)
      
      if (!isValid) {
        return // Don't select dates that don't meet the mode criteria
      }
      if (onChange) {
        // Convert to ISO format for backend
        onChange(format(date, 'yyyy-MM-dd'))
      }
    } else if (onChange) {
      onChange('')
    }
    setOpen(false)
  }

  // Handle direct input typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const removedSeparator = [...inputValue].some((character, index) =>
      character === '/' && index > 0 && index < inputValue.length - 1 &&
      raw === inputValue.slice(0, index) + inputValue.slice(index + 1)
    )
    if (removedSeparator) {
      // O input controlado repõe a barra sem juntar os segmentos nem deslocar seus dígitos.
      return
    }
    const input = raw.replace(/[^\d/]/g, '').replace(/\/{2,}/g, '/')

    if (!input.includes('/')) {
      const formatted = input.slice(0, 8)
        .replace(/^(\d{2})(\d)/, '$1/$2')
        .replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2')
      setInputValue(formatted)
      return
    }

    // Uma barra explícita encerra o dia/mês, inclusive quando tem só um dígito.
    const [day, month = '', year] = input.split('/')
    const parts = [day.slice(0, 2), month.slice(0, 2)]
    if (year !== undefined) {
      parts.push(year.slice(0, 4))
    } else if (month.length > 2) {
      // Continua a máscara quando só números são digitados após a primeira barra automática.
      parts.push(month.slice(2, 6))
    }
    setInputValue(parts.join('/'))
  }

  // Handle input blur - validate and format
  const handleInputBlur = () => {
    const raw = inputValue.trim()

    // Se o usuário limpou o campo, respeitar (não "recolocar" automaticamente).
    if (raw.length === 0) {
      if (onChange) {
        onChange('')
      }
      setInputValue('')
      return
    }

    // Normaliza possíveis barras duplicadas antes de validar.
    const normalized = raw.replace(/\/{2,}/g, '/')

    // Aceita:
    // - dd/MM/yyyy (completo)
    // - d/M/yyyy (completo, comum ao digitar)
    // - ddMMyyyy (somente números)
    const digitsOnly = normalized.replace(/\D/g, '')

    let parsed: Date | null = null

    if (/^\d{8}$/.test(digitsOnly) && !normalized.includes('/')) {
      // ddMMyyyy
      const dd = digitsOnly.slice(0, 2)
      const mm = digitsOnly.slice(2, 4)
      const yyyy = digitsOnly.slice(4, 8)
      const formatted = `${dd}/${mm}/${yyyy}`
      const d = parse(formatted, 'dd/MM/yyyy', new Date())
      parsed = isValid(d) ? d : null
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(normalized)) {
      // d/M/yyyy (aceita 1 ou 2 dígitos para dia/mês)
      const d = parse(normalized, 'd/M/yyyy', new Date())
      parsed = isValid(d) ? d : null
    }

    if (!parsed) {
      // Data inválida/incompleta -> volta para o último valor válido
      if (dateValue) {
        setInputValue(format(dateValue, 'dd/MM/yyyy', { locale: ptBR }))
      } else {
        setInputValue('')
      }
      return
    }

    // Validar restrição de período apenas no blur (evita truncar durante digitação).
    const meetsMode = mode === 'future-only'
      ? isDateAfterBudgetPeriod(parsed)
      : isDateWithinBudgetPeriod(parsed)

    if (!meetsMode) {
      if (dateValue) {
        setInputValue(format(dateValue, 'dd/MM/yyyy', { locale: ptBR }))
      } else {
        setInputValue('')
      }
      return
    }

    // Formata/normaliza no blur e emite em ISO para o backend.
    setInputValue(format(parsed, 'dd/MM/yyyy', { locale: ptBR }))
    setViewDate(parsed)
    if (onChange) {
      onChange(format(parsed, 'yyyy-MM-dd'))
    }
  }

  // Generate year options
  const years = React.useMemo(() => {
    const result = []
    for (let year = minYear; year <= maxYear; year++) {
      result.push(year)
    }
    return result
  }, [minYear, maxYear])

  // Months for selector
  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril",
    "Maio", "Junho", "Julho", "Agosto",
    "Setembro", "Outubro", "Novembro", "Dezembro"
  ]

  // Handle year change
  const handleYearChange = (yearStr: string) => {
    const newYear = Number.parseInt(yearStr, 10)
    const newDate = setYear(viewDate, newYear)
    setViewDate(newDate)
  }

  // Handle month change
  const handleMonthChange = (monthStr: string) => {
    const newMonth = Number.parseInt(monthStr, 10)
    const newDate = setMonth(viewDate, newMonth)
    setViewDate(newDate)
  }

  // Navigate years quickly
  const navigateYear = (delta: number) => {
    const newDate = setYear(viewDate, getYear(viewDate) + delta)
    setViewDate(newDate)
  }

  // Disabled dates function - based on mode
  const disabledDates = React.useMemo(() => {
    if (mode === 'future-only') {
      // For future-only mode: disable dates on or before the budget period end
      if (!periodEnd) return undefined
      return (date: Date) => {
        const d = startOfDay(date)
        return d <= periodEnd
      }
    } else {
      // For budget-period mode: only dates within budget period are selectable
      if (!periodStart || !periodEnd) return undefined
      return (date: Date) => {
        const d = startOfDay(date)
        return d < periodStart || d > periodEnd
      }
    }
  }, [periodStart, periodEnd, mode])

  // Date modifiers for highlighting budget period and today
  const modifiers = React.useMemo(() => {
    const mods: Record<string, Date[]> = {
      periodStart: periodStart ? [periodStart] : [],
      periodEnd: periodEnd ? [periodEnd] : [],
    }

    // Add period range days (only for budget-period mode)
    if (mode === 'budget-period' && periodStart && periodEnd) {
      const periodDays: Date[] = []
      const outsidePeriodDays: Date[] = []
      const currentDate = new Date(periodStart)
      // Start from beginning of month of periodStart
      currentDate.setDate(1)
      
      // Get end of month of periodEnd
      const endOfPeriodMonth = new Date(periodEnd)
      endOfPeriodMonth.setDate(1)
      endOfPeriodMonth.setMonth(endOfPeriodMonth.getMonth() + 1)
      endOfPeriodMonth.setDate(0)

      while (currentDate <= endOfPeriodMonth) {
        const d = startOfDay(currentDate)
        if (d >= periodStart && d <= periodEnd) {
          periodDays.push(new Date(d))
        } else {
          outsidePeriodDays.push(new Date(d))
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }
      mods.inPeriod = periodDays
      mods.outsidePeriod = outsidePeriodDays
    }

    return mods
  }, [periodStart, periodEnd, mode])

  // Custom modifier class names
  const modifiersClassNames = {
    inPeriod: 'bg-primary/10',
    outsidePeriod: 'text-muted-foreground opacity-50',
    periodStart: 'bg-primary text-foreground rounded-l-full font-bold',
    periodEnd: 'bg-primary text-foreground rounded-r-full font-bold',
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            id={id}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            className={cn(
              "w-full pr-10",
              className
            )}
            maxLength={10}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              setOpen(!open)
            }}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 pb-2 space-y-2">
          {/* Quick Year/Month Selector */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => navigateYear(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
              <ChevronLeft className="h-4 w-4 -ml-2" />
            </Button>
            
            <Select
              value={getMonth(viewDate).toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="h-7 flex-1 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={getYear(viewDate).toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="h-7 w-20 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => navigateYear(1)}
            >
              <ChevronRight className="h-4 w-4" />
              <ChevronRight className="h-4 w-4 -ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Calendar - Single or Dual based on showSingleCalendar prop */}
        <DayPicker
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          month={viewDate}
          onMonthChange={setViewDate}
          initialFocus
          locale={ptBR}
          numberOfMonths={showSingleCalendar ? 1 : 2}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          showOutsideDays
          disabled={disabledDates}
          className="!p-0"
          classNames={{
            months: showSingleCalendar ? '' : 'flex flex-col sm:flex-row gap-2',
            month: 'flex-1 space-y-2',
            caption: 'flex justify-center pt-1 relative items-center h-8',
            caption_label: 'text-sm font-semibold capitalize text-foreground',
            nav: 'flex items-center gap-1',
            nav_button: cn(
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-muted'
            ),
            nav_button_previous: 'absolute left-1',
            nav_button_next: 'absolute right-1',
            table: 'w-full border-collapse',
            head_row: 'flex',
            head_cell:
              'text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center',
            row: 'flex w-full mt-2',
            cell: cn(
              'flex-1 text-center text-sm p-0 relative',
              '[&:has([aria-selected])]:bg-accent',
              'first:[&:has([aria-selected])]:rounded-l-md',
              'last:[&:has([aria-selected])]:rounded-r-md',
              'focus-within:relative focus-within:z-20'
            ),
            day: cn(
              'h-8 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-muted/50'
            ),
            day_selected:
              'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
            day_today:
              'relative z-0 text-white font-bold after:content-[""] after:absolute after:left-1/2 after:top-1/2 after:h-6 after:w-6 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-emerald-700 dark:after:bg-emerald-600 after:z-[-1]',
            day_outside: 'text-muted-foreground opacity-50',
            day_disabled: 'text-muted-foreground opacity-50',
            day_hidden: 'invisible',
          }}
          components={{ IconLeft: IconeAnterior, IconRight: IconeProximo }}
        />
      </PopoverContent>
    </Popover>
  )
}
